import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFunnels, getFunnelAnalytics, getFunnelConfig, setFunnelConfig, getFunnelSources } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Overline } from "@/components/site/Primitives";
import { Copy, ExternalLink, Check, BarChart3, Settings, X, Loader2, Radio } from "lucide-react";
import { toast } from "sonner";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function ConfigModal({ slug, title, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getFunnelConfig(slug).then(setCfg).catch(() => setCfg({})); }, [slug]);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const ends = cfg.countdown_ends_at ? new Date(cfg.countdown_ends_at).toISOString() : "";
      await setFunnelConfig(slug, {
        headline_a: cfg.headline_a || "", headline_b: cfg.headline_b || "",
        ab_enabled: !!cfg.ab_enabled, countdown_enabled: !!cfg.countdown_enabled,
        countdown_ends_at: ends, countdown_message: cfg.countdown_message || "",
      });
      toast.success("Funnel settings saved."); onClose();
    } catch { toast.error("Could not save. Are you signed in as admin?"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-brand-charcoal/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-brand-cream w-full max-w-lg max-h-[90vh] overflow-y-auto border border-brand-linen shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="funnel-config-modal">
        <div className="flex items-center justify-between px-6 h-16 border-b border-brand-linen sticky top-0 bg-brand-cream">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={onClose} data-testid="funnel-config-close"><X size={20} /></button>
        </div>
        {!cfg ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-brand-sage" /></div> : (
          <div className="p-6 space-y-6">
            <div>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={!!cfg.ab_enabled} onChange={(e) => set("ab_enabled", e.target.checked)} data-testid="cfg-ab-enabled" className="w-4 h-4 accent-brand-olive" />
                <span className="font-serif text-lg">A/B test two headlines</span>
              </label>
              <p className="font-body text-sm text-brand-muted mb-3">Agnes serves each headline 50/50, then favours the higher-converting one automatically.</p>
              <input value={cfg.headline_a || ""} onChange={(e) => set("headline_a", e.target.value)} data-testid="cfg-headline-a" placeholder="Headline A (leave blank to use default title)"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body mb-2 focus:outline-none focus:border-brand-gold" />
              <input value={cfg.headline_b || ""} onChange={(e) => set("headline_b", e.target.value)} data-testid="cfg-headline-b" placeholder="Headline B"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body focus:outline-none focus:border-brand-gold" />
            </div>
            <div className="border-t border-brand-linen pt-6">
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={!!cfg.countdown_enabled} onChange={(e) => set("countdown_enabled", e.target.checked)} data-testid="cfg-countdown-enabled" className="w-4 h-4 accent-brand-olive" />
                <span className="font-serif text-lg">Limited-time countdown banner</span>
              </label>
              <input value={cfg.countdown_message || ""} onChange={(e) => set("countdown_message", e.target.value)} data-testid="cfg-countdown-message" placeholder="Banner message (e.g. Free gift ends soon)"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body mb-2 focus:outline-none focus:border-brand-gold" />
              <label className="font-sans text-[0.66rem] uppercase tracking-[0.12em] text-brand-muted">Ends at</label>
              <input type="datetime-local" value={toLocalInput(cfg.countdown_ends_at)} onChange={(e) => set("countdown_ends_at", e.target.value)} data-testid="cfg-countdown-ends"
                className="w-full bg-brand-warm-white border border-brand-linen px-4 py-3 font-body mt-1 focus:outline-none focus:border-brand-gold" />
            </div>
            <button onClick={save} disabled={saving} data-testid="funnel-config-save"
              className="w-full bg-brand-olive text-brand-cream py-3.5 font-sans text-xs uppercase tracking-[0.16em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MarketingFunnels() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [funnels, setFunnels] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [sources, setSources] = useState([]);
  const [copied, setCopied] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => { getFunnels().then(setFunnels).catch(() => {}); }, []);
  const loadAnalytics = () => {
    if (isAdmin) {
      getFunnelAnalytics().then((rows) => setAnalytics(Object.fromEntries(rows.map((r) => [r.slug, r])))).catch(() => {});
      getFunnelSources().then(setSources).catch(() => {});
    }
  };
  useEffect(loadAnalytics, [isAdmin, editing]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${origin}/f/${slug}`).then(() => {
      setCopied(slug); toast.success("Landing-page link copied.");
      setTimeout(() => setCopied(null), 2000);
    }).catch(() => toast.error("Could not copy the link."));
  };

  const totals = Object.values(analytics).reduce((a, r) => ({
    views: a.views + r.views, clicks: a.clicks + r.cta_clicks, conv: a.conv + r.conversions,
  }), { views: 0, clicks: 0, conv: 0 });

  const groups = [
    { key: "Membership", label: "Membership & Subscriptions", match: (f) => f.type === "subscription" },
    { key: "Bundle", label: "The Whole Library", match: (f) => f.type === "bundle" },
    { key: "digital", label: "Individual Editions", match: (f) => f.type === "digital" },
  ];

  return (
    <div className="bg-brand-cream" data-testid="marketing-funnels-page">
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-8">
        <Overline>For the cottage keeper</Overline>
        <h1 className="text-4xl sm:text-5xl font-light mt-4 mb-4">Marketing Funnels</h1>
        <p className="font-body text-brand-body text-lg leading-relaxed max-w-2xl">
          Every offer has its own dedicated landing page — minimal, single-minded, and wired straight to checkout,
          the newsletter and member accounts. Grab a link for your ads, emails or social posts.
        </p>
      </section>

      {isAdmin && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-6" data-testid="funnel-analytics-summary">
          <div className="grid grid-cols-3 gap-4 max-w-xl">
            {[["Views", totals.views], ["Checkout clicks", totals.clicks], ["Sales", totals.conv]].map(([l, v]) => (
              <div key={l} className="bg-brand-warm-white border border-brand-linen p-5 text-center">
                <div className="font-serif text-3xl text-brand-charcoal">{v}</div>
                <div className="font-sans text-[0.6rem] uppercase tracking-[0.12em] text-brand-muted mt-1 flex items-center justify-center gap-1"><BarChart3 size={12} /> {l}</div>
              </div>
            ))}
          </div>

          {sources.length > 0 && (
            <div className="mt-8" data-testid="funnel-sources-panel">
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Radio size={18} className="text-brand-sage" /> Traffic sources — which channel converts</h2>
              <div className="bg-brand-warm-white border border-brand-linen overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="font-sans text-[0.6rem] uppercase tracking-[0.12em] text-brand-muted border-b border-brand-linen">
                      <th className="px-5 py-3 font-normal">Source</th>
                      <th className="px-5 py-3 font-normal text-right">Views</th>
                      <th className="px-5 py-3 font-normal text-right">Clicks</th>
                      <th className="px-5 py-3 font-normal text-right">Sales</th>
                      <th className="px-5 py-3 font-normal text-right">Conv. rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr key={s.source} className="border-b border-brand-linen last:border-0 font-body text-brand-body" data-testid={`source-row-${s.source}`}>
                        <td className="px-5 py-3 text-brand-charcoal capitalize">{s.source}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{s.views}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{s.cta_clicks}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-brand-olive">{s.conversions}</td>
                        <td className="px-5 py-3 text-right tabular-nums">{s.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="font-body text-xs text-brand-muted mt-2">Tag your links with <code className="font-mono">?utm_source=instagram</code> (or <code className="font-mono">?ref=…</code>) to see each channel here.</p>
            </div>
          )}
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24 space-y-14">
        {groups.map((g) => {
          const items = funnels.filter(g.match);
          if (items.length === 0) return null;
          return (
            <div key={g.key}>
              <h2 className="font-serif text-2xl mb-5 pb-2 border-b border-brand-linen">{g.label}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((f) => {
                  const a = analytics[f.slug];
                  return (
                    <div key={f.slug} className="bg-brand-warm-white border border-brand-linen p-5 flex flex-col" data-testid={`funnel-hub-item-${f.slug}`}>
                      <div className="flex-1">
                        <h3 className="font-serif text-xl leading-snug">{f.title}</h3>
                        <p className="font-sans text-[0.62rem] uppercase tracking-[0.12em] text-brand-muted mt-2">
                          {f.category} · {f.type === "subscription" ? `$${f.price}/mo` : `$${Number(f.price).toFixed(2)}`}
                        </p>
                        {isAdmin && a && (
                          <div className="flex gap-4 mt-3 font-sans text-[0.62rem] uppercase tracking-[0.1em] text-brand-body" data-testid={`funnel-stats-${f.slug}`}>
                            <span>{a.views} views</span><span>{a.cta_clicks} clicks</span><span className="text-brand-olive">{a.conversions} sold · {a.conversion_rate}%</span>
                          </div>
                        )}
                        <code className="block mt-3 font-mono text-xs text-brand-body bg-brand-cream border border-brand-linen px-3 py-2 truncate">/f/{f.slug}</code>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => copyLink(f.slug)} data-testid={`funnel-copy-${f.slug}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 border border-brand-gold text-brand-charcoal py-2.5 font-sans text-[0.64rem] uppercase tracking-[0.08em] hover:bg-brand-linen transition-colors">
                          {copied === f.slug ? <Check size={13} /> : <Copy size={13} />} {copied === f.slug ? "Copied" : "Copy"}
                        </button>
                        {isAdmin && (
                          <button onClick={() => setEditing(f)} data-testid={`funnel-edit-${f.slug}`}
                            className="inline-flex items-center justify-center border border-brand-linen text-brand-charcoal px-3 py-2.5 hover:bg-brand-linen transition-colors">
                            <Settings size={14} />
                          </button>
                        )}
                        <Link to={`/f/${f.slug}`} target="_blank" data-testid={`funnel-open-${f.slug}`}
                          className="inline-flex items-center justify-center gap-2 bg-brand-olive text-brand-cream px-4 py-2.5 font-sans text-[0.64rem] uppercase tracking-[0.08em] hover:bg-brand-charcoal transition-colors">
                          <ExternalLink size={13} /> View
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {editing && <ConfigModal slug={editing.slug} title={editing.title} onClose={() => setEditing(null)} />}
    </div>
  );
}
